import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { apiErrorText } from 'Utils/errorText';
import { READ_ONLY_TITLE } from 'Auth/permissions';
import { fmtNum } from 'TestDataManagement/tdmUtils';
import {
  getGraphNode, updateDataGraph, tableLabel, graphHasHopLimit, DEFAULT_MAX_HOPS, graphUpdateBody, withRowFilter,
  OUTCOMES, objectsProblem, getDraftReport, getRuleSuggestions, withObject, withoutObject,
} from './dataGraphApi';
import RuleCell from './RuleCell';
import ObjectPicker from './ObjectPicker';
import useGraphDraft, { formOf } from './useGraphDraft';
import { reportNoun } from './ObjectReport';

/**
 * Where a source keeps the key reads schema.table / column for a database, database.collection /
 * field path for MongoDB, Object / field API name for Salesforce. Same value, different home: the
 * placeholder says which one the consultant is in.
 */
export const keyTablePlaceholder = (engine) => {
  const e = String(engine || '').toLowerCase();
  if (e === 'salesforce') return 'Contact';
  return e === 'mongodb' ? 'portal.members' : 'public.person';
};
export const keyColumnPlaceholder = (engine) => {
  const e = String(engine || '').toLowerCase();
  if (e === 'salesforce') return 'Person_Source_Value__c (field API name)';
  return e === 'mongodb' ? 'person_source_value (field path)' : 'person_source_value';
};

/** The draft check waits this long after the last edit before asking the platform. */
export const DRAFT_DEBOUNCE_MS = 500;

const lower = (s) => String(s || '').toLowerCase();

/** Why the extra starts cannot be saved yet, or null (the platform's own 400s, said before the save). */
export const startsProblem = (starts, keyTable, keyColumn) => {
  const list = starts || [];
  if (list.length === 0) return null;
  if (!String(keyTable || '').trim() || !String(keyColumn || '').trim()) return 'Extra starts need a key table and column: they match the selected ids.';
  const seen = new Set();
  for (let i = 0; i < list.length; i += 1) {
    const t = String(list[i].table || '').trim();
    const c = String(list[i].column || '').trim();
    if (!t || !c) return 'Every extra start needs an object and a column.';
    if (lower(t) === lower(keyTable) && lower(c) === lower(keyColumn)) return `${t}.${c} is the key itself, not an extra start.`;
    const k = `${lower(t)}.${lower(c)}`;
    if (seen.has(k)) return `${t}.${c} is an extra start twice.`;
    seen.add(k);
  }
  return null;
};

// One extra start: object (the same picker as the key table) + column.
const StartRow = ({ start, tables, columnsOf, disabled, onChange, onRemove, testId }) => {
  const [columns, setColumns] = useState([]);
  useEffect(() => {
    let alive = true;
    setColumns([]);
    if (!start.table) return undefined;
    Promise.resolve(columnsOf(start.table)).then((c) => { if (alive) setColumns(c || []); }).catch(() => {});
    return () => { alive = false; };
  }, [start.table, columnsOf]);
  return (
    <div className='dg-starts__row' data-testid={testId}>
      <ObjectPicker value={start.table} onChange={(name) => onChange({ table: name, column: tables.length > 0 ? '' : start.column })}
        tables={tables} disabled={disabled} label='Start object' placeholder='object' listName='start object' testId={`${testId}-table`} />
      {columns.length > 0 ? (
        <select className='dg-input' value={start.column} onChange={(e) => onChange({ table: start.table, column: e.target.value })} disabled={disabled}
          data-testid={`${testId}-column`} aria-label='Start column'>
          <option value=''>column…</option>
          {start.column && !columns.includes(start.column) && <option value={start.column}>{start.column}</option>}
          {columns.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      ) : (
        <input className='dg-input' value={start.column} onChange={(e) => onChange({ table: start.table, column: e.target.value })} disabled={disabled}
          placeholder='column holding the selected ids' data-testid={`${testId}-column`} aria-label='Start column' />
      )}
      {!disabled && <Button size='sm' variant='link' className='p-0' onClick={onRemove} data-testid={`${testId}-remove`}>Remove</Button>}
    </div>
  );
};

// A data graph's settings, the page's first panel (laid out 2026-11-04, 4.2.4): the name subsets pick it
// by, the key name (a subset's shared key name when this source is the parent) and description; the key —
// its table is where a subset's row selection starts, with other sources rows are matched on it — picked
// from the profiled objects under the kind filters; the extra starts (3.1: other columns the selected ids
// are matched in); the graph's manual links (`manualLinks`, a slot); what the graph asks for (contract 3.0):
// EVERYTHING CONNECTED to the key table (relational: to a hop limit; MongoDB and Salesforce walk without
// one), or A LIST of objects; and the object selection (`objectsView`, a slot: the page's object or table
// list) folded in an accordion whose head keeps the count — a tick lists the object, and a listed row's
// "How it's reached" cell (RuleCell) holds its rules, given or derived at the Test. One Save for all of it.
// The platform's draft check runs as the settings are edited: its tags show on
// the list, and a list’s ranked rule suggestions (the alternatives in a row’s Edit dialog) come with it. The Test
// (the page's last panel) holds the counts and what the list needs. Table names come from the graph's node
// list, columns from the node API. `draft` is the page's one draft (useGraphDraft); without one the panel
// keeps its own.
const GraphConfigPanel = ({ graph, connection, tables, canEdit, onSaved, onDraftReport, draft: pageDraft, manualLinks, objectsView }) => {
  const engine = (connection && (connection.type || connection.engine)) || (graph && graph.engine);
  const noun = reportNoun(engine);
  const hopLimited = graphHasHopLimit(connection || { datasourceType: graph && graph.datasourceType });
  const ownDraft = useGraphDraft(graph, { hopLimited, onSaved });
  const { form, set, setObjects, setStarts, dirty, busy, notice, setNotice, save, body: draftBody } = pageDraft || ownDraft;
  const [columns, setColumns] = useState([]);
  const [draft, setDraft] = useState({ report: null, error: null, loading: false });
  const [suggest, setSuggest] = useState({ items: null, loading: false, error: null });
  const [filterBusy, setFilterBusy] = useState(false);
  const [objectsOpen, setObjectsOpen] = useState(true);

  const list = form.outcome === 'LIST';
  const dataSourceId = graph && graph.dataSourceId;

  // Columns per table, read once each from the node API (the key picker and every rule share them).
  const columnCache = useRef(new Map());
  useEffect(() => { columnCache.current = new Map(); }, [dataSourceId]);
  const columnsOf = useCallback((name) => {
    const key = lower(name);
    if (!key || dataSourceId == null) return Promise.resolve([]);
    if (!columnCache.current.has(key)) {
      const node = (tables || []).find((n) => lower(tableLabel(n)) === key);
      const dot = String(name).indexOf('.');
      const table = node ? node.table : (dot > 0 ? String(name).slice(dot + 1) : String(name));
      const schema = node ? node.schema : (dot > 0 ? String(name).slice(0, dot) : undefined);
      columnCache.current.set(key, getGraphNode(dataSourceId, table, schema)
        .then((d) => ((d && d.columns) || []).map((c) => c.name))
        .catch(() => { columnCache.current.delete(key); return []; }));
    }
    return columnCache.current.get(key);
  }, [dataSourceId, tables]);

  const keyNode = useMemo(
    () => (tables || []).find((n) => lower(tableLabel(n)) === lower(form.keyTable.trim())),
    [tables, form.keyTable],
  );
  useEffect(() => {
    if (!graph || !form.keyTable.trim()) { setColumns([]); return undefined; }
    let alive = true;
    columnsOf(form.keyTable.trim()).then((c) => { if (alive) setColumns(c || []); });
    return () => { alive = false; };
  }, [graph, form.keyTable, columnsOf]);

  const halfKey = Boolean(form.keyTable.trim()) !== Boolean(form.keyColumn.trim());
  const hops = form.maxHops.trim();
  const showHops = hopLimited && !list;
  const badHops = showHops && hops !== '' && !/^\d+$/.test(hops);
  const listProblem = list
    ? (!form.keyTable.trim() || !form.keyColumn.trim()
      ? 'A list needs a key table and column: the selection starts there.'
      : objectsProblem(form.objects, form.keyTable.trim()))
    : null;
  const startProblem = startsProblem(form.starts, form.keyTable, form.keyColumn);

  // The draft check (contract 3.0): the isolation report of what the editor holds, saved or not, from the plan
  // alone — its findings before the Test, with the planner's derived rules for objects that have none (3.1).
  // Re-asked DRAFT_DEBOUNCE_MS after the last edit that changes the plan (name and description do not);
  // skipped while the settings could not be saved anyway. A list's ranked rule suggestions come with it, so a
  // derived rule's alternatives are a click away.
  const planKey = JSON.stringify([form.outcome, form.keyTable.trim(), form.keyColumn.trim(), showHops ? hops : '', form.objects, form.starts]);
  const canDraft = Boolean(graph) && Boolean(form.keyTable.trim()) && Boolean(form.keyColumn.trim()) && !halfKey && !badHops && !listProblem && !startProblem;
  const askSuggestions = useCallback(async () => {
    setSuggest({ items: null, loading: true, error: null });
    try {
      const items = await getRuleSuggestions({ graphId: graph.id, ...draftBody, tableFilters: graph.tableFilters || [] });
      setSuggest({ items, loading: false, error: null });
    } catch (e) {
      setSuggest({ items: null, loading: false, error: apiErrorText(e, 'Could not suggest rules') });
    }
  }, [graph, draftBody]);
  const draftSeq = useRef(0);
  useEffect(() => {
    if (!canDraft) { setDraft({ report: null, error: null, loading: false }); return undefined; }
    const seq = draftSeq.current + 1;
    draftSeq.current = seq;
    setDraft((d) => ({ ...d, loading: true }));
    const t = setTimeout(() => {
      getDraftReport({ graphId: graph.id, ...draftBody, tableFilters: graph.tableFilters || [] })
        .then((report) => { if (draftSeq.current === seq) setDraft({ report: report || null, error: null, loading: false }); })
        .catch((e) => { if (draftSeq.current === seq) setDraft({ report: null, error: apiErrorText(e, 'Could not check the list'), loading: false }); });
      if (list && form.objects.length > 0) askSuggestions();
    }, DRAFT_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // planKey stands for every field of `form` the plan reads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planKey, canDraft, graph]);
  useEffect(() => { if (onDraftReport) onDraftReport(draft.report); }, [draft.report, onDraftReport]);

  const removeFilter = async (table) => {
    setFilterBusy(true);
    setNotice(null);
    try {
      const saved = await updateDataGraph(graph.id, graphUpdateBody(graph, withRowFilter(graph, table, '')));
      if (onSaved) onSaved(saved);
    } catch (e) {
      setNotice({ tone: 'error', text: apiErrorText(e, 'Could not remove the row filter') });
    } finally {
      setFilterBusy(false);
    }
  };

  const disabled = !canEdit;
  const tableNames = (tables || []).map((n) => tableLabel(n));
  const keyInProfile = !form.keyTable.trim() || tableNames.length === 0 || Boolean(keyNode);
  // The pickers hand back a name, not an event.
  const setKeyTable = useCallback((name) => set('keyTable')({ target: { value: name } }), [set]);

  // The object selection's "How it's reached" cell: a listed object's rules; the key table's "start"; nothing
  // for an unticked row. The slot renders it beside each row of the page's object or table list.
  const keyTable = form.keyTable.trim();
  const byObject = useMemo(() => {
    const m = {};
    (suggest.items || []).forEach((s) => { if (s && s.object) m[lower(s.object)] = s.candidates || []; });
    return m;
  }, [suggest.items]);
  const sourcesFor = useCallback((name) => {
    const out = [];
    if (keyTable && lower(keyTable) !== lower(name)) out.push(keyTable);
    form.objects.forEach((o) => { if (lower(o.name) !== lower(name) && !out.some((s) => lower(s) === lower(o.name))) out.push(o.name); });
    return out;
  }, [keyTable, form.objects]);
  const ruleCellOf = useCallback((name) => {
    if (!list) return null;
    const i = form.objects.findIndex((o) => lower(o.name) === lower(name));
    if (i < 0) {
      return keyTable && lower(keyTable) === lower(name)
        ? <span className='dg-rule__origin dg-rule__origin--start' data-testid={`dg-object-${name}-start`} title='The key table: the subset’s selected rows'>start</span>
        : null;
    }
    const object = form.objects[i];
    return (
      <RuleCell object={object} keyTable={keyTable} sources={sourcesFor(object.name)} columnsOf={columnsOf} disabled={disabled}
        candidates={byObject[lower(object.name)]} testId={`dg-object-${object.name}`}
        onUpdate={(next) => setObjects((objects) => objects.map((x) => (lower(x.name) === lower(object.name) ? next : x)))} />
    );
  }, [list, form.objects, keyTable, sourcesFor, columnsOf, disabled, byObject, setObjects]);
  // A tick from the slot (the relational table list ticks through the page's choice; this is for a view without one).
  const toggle = useCallback((name) => setObjects((objects) => (objects.some((o) => lower(o.name) === lower(name))
    ? withoutObject(objects, name) : withObject(objects, name))), [setObjects]);
  const objectsDirty = JSON.stringify(form.objects) !== JSON.stringify(formOf(graph).objects);
  const count = form.objects.length;
  const nounPlural = `${noun.toLowerCase()}s`;

  return (
    <div className='dg-panel mb-3' data-testid='dg-config'>
      <div className='dg-panel__head'>
        <div className='dg-panel__title'>Data graph settings</div>
        <div className='dg-muted'>
          Where a subset of {connection ? connection.name : 'this source'} starts, and the {nounPlural} it should hold. TDM works out how each is reached; the Test below confirms it.
        </div>
      </div>
      <div className='dg-config dg-config--two'>
        <label className='dg-config__field'>Name
          <input className='dg-input' value={form.name} onChange={set('name')} disabled={disabled} data-testid='dg-config-name' />
        </label>
        <label className='dg-config__field' title='Shown as the shared key of a subset whose parent uses this data graph, e.g. "Member number"'>Key name
          <input className='dg-input' value={form.keyName} onChange={set('keyName')} disabled={disabled} placeholder='Member number'
            data-testid='dg-config-key-name' />
        </label>
      </div>
      <label className='dg-config__field dg-config__field--wide mt-2'>Description
        <input className='dg-input' value={form.description} onChange={set('description')} disabled={disabled}
          placeholder='What this data graph is for' data-testid='dg-config-description' />
      </label>
      <div className='dg-config mt-2'>
        <div className='dg-config__field dg-config__field--key' title={'Where a subset’s row selection starts when this source is its parent; with other sources, the table rows are matched on'}>Key table
          <ObjectPicker value={form.keyTable} onChange={setKeyTable} tables={tables} disabled={disabled} label='Key table'
            placeholder={keyTablePlaceholder(engine)} listName='Key table' testId='dg-config-key-table' />
        </div>
        <label className='dg-config__field'>Key column
          {columns.length > 0 ? (
            <select className='dg-input' value={form.keyColumn} onChange={set('keyColumn')} disabled={disabled} data-testid='dg-config-key-column'>
              <option value=''>column…</option>
              {form.keyColumn && !columns.includes(form.keyColumn) && <option value={form.keyColumn}>{form.keyColumn} (not in the latest profile)</option>}
              {columns.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          ) : (
            <input className='dg-input' value={form.keyColumn} onChange={set('keyColumn')} disabled={disabled}
              placeholder={keyColumnPlaceholder(engine)} data-testid='dg-config-key-column' />
          )}
        </label>
        {showHops && (
          <label className='dg-config__field' title='How many steps from the key table the walk follows child tables; what the reached rows reference is always included'>Max child hops
            <input className='dg-input' type='number' min='0' max='64' value={form.maxHops} onChange={set('maxHops')} disabled={disabled}
              placeholder={`${DEFAULT_MAX_HOPS} (default)`} data-testid='dg-config-max-hops' />
          </label>
        )}
        {!hopLimited && !list && (
          <div className='dg-config__field' data-testid='dg-config-no-hops'>Max child hops
            <span className='dg-muted'>No limit: the walk follows the shared key and this graph&apos;s links.</span>
          </div>
        )}
      </div>
      {!keyInProfile && (
        <div className='dg-error' data-testid='dg-config-key-missing'>{form.keyTable} is not in the latest profile: re-sync the schema, or pick another key table.</div>
      )}
      <div className='dg-starts' data-testid='dg-config-starts'>
        <div className='dg-starts__title'
          title={'Rows of another object whose column holds a selected id are start rows too — the same ids, whatever the selection (ID in, Where or All on the key table). Each becomes the rule object.column IN key table.key column; the object is listed if it is not already. An id that matches no key-table row seeds nothing.'}>
          <span className='dg-help'>Also match the selected ids in</span>
          {form.starts.length === 0 && <span className='dg-muted'>— no other columns</span>}
        </div>
        {form.starts.map((st, i) => (
          <StartRow key={i} start={st} tables={tables || []} columnsOf={columnsOf} disabled={disabled} testId={`dg-config-start-${i}`}
            onChange={(next) => setStarts(form.starts.map((x, j) => (j === i ? next : x)))}
            onRemove={() => setStarts(form.starts.filter((_, j) => j !== i))} />
        ))}
        {!disabled && (
          <Button size='sm' variant='link' className='p-0 mt-1' data-testid='dg-config-start-add' disabled={!form.keyTable.trim()}
            title={!form.keyTable.trim() ? 'Set the key table first: an extra start matches its selected ids' : undefined}
            onClick={() => setStarts([...form.starts, { table: '', column: '' }])}>+ Add another start</Button>
        )}
      </div>
      {(graph.warnings || []).length > 0 && (
        <ul className='dg-warnings' data-testid='dg-config-warnings'>
          {graph.warnings.map((w, i) => <li key={i}>{typeof w === 'string' ? w : (w.message || w.code)}</li>)}
        </ul>
      )}

      {manualLinks}

      <div className='dg-section' data-testid='dg-config-pulls'>
        <div className='dg-section__title'>What this data graph pulls</div>
        <div className='dg-outcome' role='radiogroup' aria-label='What this data graph pulls' data-testid='dg-config-outcome'>
          {OUTCOMES.map(([v, label]) => (
            <label key={v}>
              <input type='radio' name={`dg-outcome-${graph.id}`} value={v} checked={form.outcome === v} disabled={disabled}
                onChange={set('outcome')} data-testid={`dg-config-outcome-${v}`} />
              {label}
            </label>
          ))}
        </div>
        <div className='dg-muted dg-note mt-1' data-testid='dg-config-outcome-help'>
          {list
            ? `Only the ${nounPlural} ticked below are saved: each is reached by its rule, and what the saved rows look up among them comes along for integrity.`
            : `Everything linked below the key table${showHops ? ' to the hop limit' : ''}, plus what those rows reference.`}
        </div>
      </div>

      {objectsView && (
        <div className='dg-acc' data-testid='dg-config-objects'>
          <button type='button' className='dg-acc__head' aria-expanded={objectsOpen} onClick={() => setObjectsOpen(!objectsOpen)}
            data-testid='dg-config-objects-toggle'>
            <span className='dg-acc__title'>
              <span className='dg-caret'>{objectsOpen ? '▾' : '▸'}</span>
              {list ? (
                <>{noun}s in this data graph · <strong data-testid='dg-config-objects-count'>{fmtNum(count)}</strong> selected</>
              ) : (
                <>{noun}s</>
              )}
              {list && objectsDirty && <span className='dg-muted' data-testid='dg-config-objects-unsaved'> · unsaved</span>}
            </span>
            <span className='dg-muted dg-acc__hint'>
              {list
                ? (objectsOpen ? `Tick what the subset should hold; a ticked ${noun.toLowerCase()}’s row says how it is reached.` : 'Expand to change the selection')
                : (objectsOpen ? `Every ${noun.toLowerCase()} of the profile; the walk follows its links.` : 'Expand to explore')}
            </span>
          </button>
          {objectsOpen && (
            <div className='dg-acc__body' data-testid='dg-config-objects-body'>
              {typeof objectsView === 'function' ? objectsView({ ruleCellOf, toggle }) : objectsView}
            </div>
          )}
        </div>
      )}

      {/* The draft check's findings are the Test's to show (2026-11-04): here only its failures to run. */}
      {list && draft.error && <div className='dg-error' data-testid='dg-config-draft-error'>{draft.error}</div>}
      {list && suggest.error && <div className='dg-error' data-testid='dg-objects-suggest-error'>{suggest.error}</div>}

      {(graph.tableFilters || []).length > 0 && (
        <div className='dg-section' data-testid='dg-config-filters'>
          <div className='dg-section__title'>Row filters</div>
          {graph.tableFilters.map((f) => (
            <div className='dg-filter__row' key={f.table} data-testid={`dg-config-filter-${f.table}`}>
              <span className='dg-mono'>{f.table}</span>
              <span className='dg-mono dg-muted dg-clip' title={f.expression}>{f.expression}</span>
              {canEdit && (
                <Button size='sm' variant='link' className='p-0' disabled={busy || filterBusy} data-testid={`dg-config-filter-remove-${f.table}`}
                  onClick={() => removeFilter(f.table)}>Remove</Button>
              )}
            </div>
          ))}
        </div>
      )}
      {halfKey && <div className='dg-error' data-testid='dg-config-half-key'>A key needs both a table and a column, or neither.</div>}
      {badHops && <div className='dg-error'>Max child hops is a whole number from 0 to 64.</div>}
      {listProblem && !halfKey && <div className='dg-error' data-testid='dg-config-list-problem'>{listProblem}</div>}
      {startProblem && !halfKey && <div className='dg-error' data-testid='dg-config-starts-problem'>{startProblem}</div>}
      <div className='dg-config__actions'>
        <Button size='sm' onClick={save} data-testid='dg-config-save'
          disabled={disabled || busy || !dirty || !form.name.trim() || halfKey || badHops || Boolean(listProblem) || Boolean(startProblem)}
          title={disabled ? READ_ONLY_TITLE : undefined}>
          {busy ? 'Saving…' : 'Save settings'}
        </Button>
        {dirty && !busy && <span className='dg-muted' data-testid='dg-config-unsaved'>Unsaved changes · the Test runs the saved data graph</span>}
        {notice && <span className={notice.tone === 'error' ? 'dg-error mt-0' : 'dg-muted'} data-testid='dg-config-notice'>{notice.text}</span>}
      </div>
    </div>
  );
};

export default GraphConfigPanel;
